import React, { useState, useEffect, useRef, useContext } from 'react';
import { useRange } from 'react-instantsearch';

import DoubleRangeSlider from '../double-range-slider/DoubleRangeSlider';
import SaleItems from './SaleItems';
import GtmContext from '../../contexts/GtmContext';
import { pushToDataLayer } from '@jbhi-fi/utils/src/ga4Tracking/datalayer';

const DEBOUNCE_TIME = 400;

/**
 * Price range dropdown component
 * Params provided by connectRange via Algolia (https://www.algolia.com/doc/api-reference/widgets/range-input/react/)
 * @param {Object} currentRefinement The current refinement (min, max)
 * @param {Boolean} canRefine True/false bool to determine whether search can be further refined
 * @param {Int} min Integer determining minimum
 * @param {Int} max Integer determining maximum
 * @param {Int} nbHits Integer number of hits returned from Algolia search
 * @param {Function} refine Function to initiate refine
 * @param {Function} setPriceRange Function to set the price range (Object)
 * @param {Function} setSaleItems Function to set the sale items (Bool)
 */
const PriceRangeSlider = ({ setCurrentRefinement, nbHits, setPriceRange, showDropdown, setRangeValues }) => {
  const { refine, range, start } = useRange({ attribute: 'facets.Price' });
  const { min, max } = range;
  const currentRefinement = {
    min: Math.max(min, Number.isFinite(start[0]) ? start[0] : min),
    max: Math.min(max, Number.isFinite(start[1]) ? start[1] : max),
  };
  const [debouncedState, setDebouncedState] = useState(null);

  // Reset price range slider when min/max values change (on new search term entered for example)
  useEffect(() => {
    setRangeValues && setRangeValues({ min, max });
    refine([min, max]);
    if (min !== currentRefinement.min || max !== currentRefinement.max) {
      setPriceRange({
        min: min,
        max: max,
      });
    }
  }, [min, max]);

  useEffect(() => {
    if (
      currentRefinement.isRefined !==
      (currentRefinement && (currentRefinement.min !== min || currentRefinement.max !== max))
    ) {
      setCurrentRefinement({
        ...currentRefinement,
        isRefined: currentRefinement && (currentRefinement.min !== min || currentRefinement.max !== max),
      });
    }
  }, [currentRefinement.max, currentRefinement.min]);

  const onRefine = ({ currentMin, currentMax }) => {
    refine([currentMin, currentMax]);
    setPriceRange({
      min: currentMin,
      max: currentMax,
    });
  };

  /**
   * Render function
   */
  return (
    <DoubleRangeSlider
      showDollar={true}
      nbHits={nbHits}
      showDropdown={showDropdown}
      min={min}
      max={max}
      currentMin={currentRefinement.min}
      currentMax={currentRefinement.max || 0}
      onRefine={onRefine}
    />
  );
};

const PriceRangeContainer = ({ setPriceRange, setSaleItems, nbHits, nbSaleItems, setRangeValues, searchTerm }) => {
  const RefineDropdown = useRef(null);
  const RefineButton = useRef(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [currentRefinement, setCurrentRefinement] = useState({ isRefined: false });
  const { gtm, setGtm } = useContext(GtmContext);

  useEffect(() => {
    if (currentRefinement.isRefined && (currentRefinement.min || currentRefinement.max)) {
      setGtm({
        ...gtm,
        filters: {
          ...gtm.filters,
          price: [
            {
              type: 'range',
              min: currentRefinement.min,
              max: currentRefinement.max,
            },
          ],
        },
      });

      window.dataLayer.push({
        event: 'custom.filter.price.set',
        label: 'set',
        name: 'price',
        value: {
          min: currentRefinement.min,
          max: currentRefinement.max,
        },
        newFilters: {
          ...gtm.filters,
          price: [
            {
              min: currentRefinement.min,
              max: currentRefinement.max,
            },
          ],
        },
        oldFilters: gtm.filters,
      });
    } else if (gtm.filters) {
      setGtm({
        ...gtm,
        filters: {
          ...gtm.filters,
          price: undefined,
        },
      });
    } else {
      setGtm({
        ...gtm,
      });
    }
  }, [currentRefinement.min, currentRefinement.max, currentRefinement.isRefined]);

  // Remove the event listener on unmount
  useEffect(() => {
    return () => {
      document.body.removeEventListener('click', externalClick.bind(this));
    };
  }, []);

  /**
   * Function to handle external clicks (clicks not within the price range dropdown or button)
   */
  const externalClick = event => {
    const PriceRangeButton = RefineButton.current;
    const PriceRangeDropdown = RefineDropdown.current;

    // Close price range dropdown if clicked outside of target window (desktop)
    if (
      !event ||
      (PriceRangeDropdown &&
        !PriceRangeDropdown.contains(event.target) &&
        PriceRangeButton &&
        !PriceRangeButton.contains(event.target))
    ) {
      setShowDropdown(false);
    }
  };

  /**
   * Function to handle dropdown toggle (clicking button)
   */
  const toggleDropdown = event => {
    event.preventDefault();
    pushToDataLayer({
      event: 'open_filter_group',
      filter_group: 'price',
      search_type: 'suggested search',
      search_term: searchTerm,
    });

    if (!showDropdown === true) {
      // Add event listener for dismissing the dropdown when a user clicks away
      document.body.addEventListener('click', externalClick.bind(this));
    } else {
      // Remove event listener when dropdown hides
      document.body.removeEventListener('click', externalClick.bind(this));
    }

    setShowDropdown(!showDropdown);
  };

  return (
    <div className="search-price-range">
      <button
        type="button"
        ref={RefineButton}
        className={`search-price-range-button${showDropdown ? ' active' : ''}${
          currentRefinement.isRefined ? ' active' : ''
        }`}
        onClick={toggleDropdown.bind(this)}
      >
        {!currentRefinement.isRefined ? 'Price range' : `$${currentRefinement.min} - $${currentRefinement.max}`}
      </button>
      <div
        ref={RefineDropdown}
        className={`search-price-range-dropdown scoped-form-styles ${showDropdown && 'active'}`}
      >
        <div className="resultNum">
          {nbHits} result{nbHits !== 1 ? 's' : ''}
        </div>
        <PriceRangeSlider
          attribute="facets.Price"
          nbHits={nbHits}
          showDropdown={showDropdown}
          setPriceRange={setPriceRange}
          RefineDropdown={RefineDropdown}
          RefineButton={RefineButton}
          setCurrentRefinement={setCurrentRefinement}
          setRangeValues={setRangeValues}
        />
        <SaleItems
          attribute="onPromotion"
          setSaleItems={setSaleItems}
          nbSaleItems={nbSaleItems}
          label="Sale items"
          value="true"
        />
        <button type="submit" className="search-price-range-dropdown--search">
          Search
        </button>
      </div>
    </div>
  );
};

export default PriceRangeContainer;
